import { Swiss, Round, MatchService, MatchState, Match, LeagueFactory } from "ng-league";
import { League, User, ContestantInfo } from "@play/api-typings";
import { IMatch, ISettingsService } from "../typings/typings";
import { Store } from "@ngrx/store";
import { EntryState, RootState, getLeague, LoadLeagueOnceAction, getUser, getContestants, entryAvailable } from "@play/state";
import { IScope, ILocationService, ISCEService } from "angular";
import { filter, distinct, map, startWith, switchMap, tap } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { sortBy } from "lodash";

export class SwissResultController {
    public loading: boolean;
    public firstLoad: boolean;
    public league: Swiss;
    public rounds: Round[];
    public matches: IMatch[];
    public round: number;
    public promise: Promise<any>;
    public user: Observable<User>;
    public userContestants: Observable<ContestantInfo[]>;

    public static $inject = ["$scope", "$location", "$sce", "MatchService", "SettingsService", "store"];
    constructor(
        private $scope: IScope,
        private $location: ILocationService,
        private $sce: ISCEService,
        private MatchService: MatchService,
        public SettingsService: ISettingsService,
        private store: Store<RootState>
    ) {
        this.loading = true;
        this.matches = [];
        this.league = null;
        this.firstLoad = true;

        if (typeof $location.search().round !== "undefined" && parseInt($location.search().round) && $location.search().round > 0) {
            this.round = parseInt($location.search().round);
            this.fetchRoundData(this.round);
        }

        this.$scope.$on("$locationChangeSuccess", () => {
            if (typeof $location.search().round !== "undefined" || parseInt($location.search().round)) {
                this.fetchRoundData(parseInt($location.search().round));
            }
        });

        this.store.dispatch(new LoadLeagueOnceAction(+this.SettingsService.Result.leagueId));
        this.store.select(getLeague(+this.SettingsService.Result.leagueId)).subscribe((league: EntryState<League>) => {
            if (!league.entry) {
                return;
            }

            this.userContestants = this.user.pipe(
                switchMap(user => {
                    if (!user) {
                        return of(null);
                    }

                    return this.store.select(getContestants(league.entry.id, +user.id)).pipe(
                        filter(entryAvailable),
                        map(contestants => contestants.entry)
                    );
                })
            );

            this.league = LeagueFactory.getLeagueByType(league.entry) as any;
            this.firstLoad = false;
            this.rounds = this.league.getRounds();

            if (typeof $location.search().round === "undefined" || !parseInt($location.search().round) || $location.search().round <= 0) {
                this.round = this.league.getCurrentRound();
                this.fetchRoundData(this.round, true);
            }
        });

        this.user = this.store.select(getUser).pipe(
            filter(user => user && Boolean(!user.loading || user.entry)),
            distinct(user => Boolean(user.entry)),
            map(user => user.entry),
            startWith({} as User)
        );
    }

    private moveUserMatchToTop(matchGroups, contestants: ContestantInfo[]): IMatch[] {
        return matchGroups.map(matchGroup => {
            if (contestants) {
                contestants.forEach(contestant => {
                    matchGroup.matches = sortBy(matchGroup.matches, [
                        function(match) {
                            const contestant1 = match.getMatchRankingEntries()[0].getContestant();
                            const contestant2 = match.getMatchRankingEntries()[1].getContestant();

                            return contestant1.getId() === contestant.id || contestant2.getId() === contestant.id;
                        },
                    ]);
                });
            }

            return matchGroup;
        });
    }

    public highlight(result, search) {
        if (!search) {
            return this.$sce.trustAsHtml(result);
        }

        return this.$sce.trustAsHtml(result.replace(new RegExp(search, "gi"), '<span class="results__search__highlight">$&</span>'));
    }

    public fetchRoundData(index: number, firstLoad: boolean = false) {
        this.round = index;
        if (this.$location.search().round != this.round) {
            if (firstLoad) {
                this.$location.search("round", this.round).replace();
            } else {
                this.$location.search("round", this.round);
            }

            return;
        }

        this.matches = [];
        this.loading = true;

        this.promise = this.MatchService.getMatchesInRound(parseInt(this.SettingsService.Result.leagueId, 10), this.round).then(
            (matches: Match[]) => {
                this.matches = this.groupMatchesByDate(matches);
                this.loading = false;
                this.firstLoad = false;

                this.userContestants
                    .pipe(map(contestants => this.moveUserMatchToTop(this.matches, contestants)))
                    .subscribe(sortedMatches => {
                        this.matches = sortedMatches;
                    });
            }
        ) as any;
    }

    private groupMatchesByDate(matches: Match[]) {
        let groupedMatches = [];

        matches.sort((match1: Match, match2: Match) => {
            // closed 1st
            if (match1.getStatus() === MatchState.CLOSED && match2.getStatus() !== MatchState.CLOSED) {
                return -1;
            } else if (match1.getStatus() !== MatchState.CLOSED && match2.getStatus() === MatchState.CLOSED) {
                return 1;
            }

            // open 2nd
            if (match1.getStatus() === MatchState.OPEN && match2.getStatus() !== MatchState.OPEN) {
                return -1;
            } else if (match1.getStatus() !== MatchState.OPEN && match2.getStatus() === MatchState.OPEN) {
                return 1;
            }

            // challenge 3rd
            if (match1.getStatus() === MatchState.CHALLENGE && match2.getStatus() !== MatchState.CHALLENGE) {
                return -1;
            } else if (match1.getStatus() !== MatchState.CHALLENGE && match2.getStatus() === MatchState.CHALLENGE) {
                return 1;
            }

            // exception 4th
            if (match1.getStatus() === MatchState.EXCEPTION && match2.getStatus() !== MatchState.EXCEPTION) {
                return -1;
            } else if (match1.getStatus() !== MatchState.EXCEPTION && match2.getStatus() === MatchState.EXCEPTION) {
                return 1;
            }

            // protest 5th
            if (match1.getStatus() === MatchState.PROTEST && match2.getStatus() !== MatchState.PROTEST) {
                return -1;
            } else if (match1.getStatus() !== MatchState.PROTEST && match2.getStatus() === MatchState.PROTEST) {
                return 1;
            }

            // id 6th
            if (match1.getId() > match2.getId()) {
                return -1;
            }

            return 1;
        });

        matches.forEach((match: Match) => {
            let date = match.getBeginAt();

            // check if date already exists
            let index = groupedMatches
                .map(element => {
                    return element.beginAt;
                })
                .indexOf(date.getTime());

            if (index === -1) {
                groupedMatches.push({
                    beginAt: date.getTime(),
                    matches: [],
                });

                index = groupedMatches.length - 1;
            }

            groupedMatches[index].matches.push(match);
        });

        return groupedMatches;
    }
}
